// Local stub for the `workspace_assignment` table and its admin editor. No
// real endpoint exists yet, so the admin page reads and writes this in-memory
// store directly; `resolveWorkspace` in `workflow-stub.ts` reads it too, so an
// edit here takes effect immediately on the live detail page — this is
// genuinely the resolution mechanism, not a decorative form.

import { WorkspaceCode } from '@/domain/workflow.types'
import { DEFAULT_WORKFLOW_STATUSES, WORKSPACE_BY_STATUS } from '@/domain/workflow-stub.seed'

/** A single `workspace_assignment` row — `assetTypeCode: null` is the status's own default, not an override. */
export interface WorkspaceAssignment {
	statusId: string
	assetTypeCode: string | null
	workspaceCode: WorkspaceCode
}

let assignments: WorkspaceAssignment[] = DEFAULT_WORKFLOW_STATUSES.map((status) => ({
	statusId: status.id,
	assetTypeCode: null,
	workspaceCode: WORKSPACE_BY_STATUS[status.id] ?? WorkspaceCode.QUEUE_CARD,
}))

const listeners = new Set<() => void>()
let version = 0
export function subscribeWorkspaceAssignments(listener: () => void): () => void {
	listeners.add(listener)
	return () => listeners.delete(listener)
}
export function getWorkspaceAssignmentsVersion(): number {
	return version
}
function notify(): void {
	version++
	for (const listener of listeners) listener()
}

export function listWorkspaceAssignments(): readonly WorkspaceAssignment[] {
	return assignments
}

/** A status's own default assignment always exists — never removable, only re-pointed, so a status can never end up with no resolvable workspace at all. */
export function setDefaultWorkspace(statusId: string, workspaceCode: WorkspaceCode): void {
	assignments = assignments.map((a) =>
		a.statusId === statusId && a.assetTypeCode === null ? { ...a, workspaceCode } : a,
	)
	notify()
}

/** Adds or re-points an asset-type override for a status. */
export function setOverride(statusId: string, assetTypeCode: string, workspaceCode: WorkspaceCode): void {
	const idx = assignments.findIndex((a) => a.statusId === statusId && a.assetTypeCode === assetTypeCode)
	if (idx >= 0) {
		assignments = assignments.map((a, i) => (i === idx ? { ...a, workspaceCode } : a))
	} else {
		assignments = [...assignments, { statusId, assetTypeCode, workspaceCode }]
	}
	notify()
}

export function removeOverride(statusId: string, assetTypeCode: string): void {
	assignments = assignments.filter((a) => !(a.statusId === statusId && a.assetTypeCode === assetTypeCode))
	notify()
}

/** Resolution rule: an asset-type override wins; otherwise the status's default applies. */
export function resolveWorkspaceCodeFor(statusId: string, assetTypeCode: string | null): WorkspaceCode {
	const override = assetTypeCode
		? assignments.find((a) => a.statusId === statusId && a.assetTypeCode === assetTypeCode)
		: undefined
	if (override) return override.workspaceCode
	return (
		assignments.find((a) => a.statusId === statusId && a.assetTypeCode === null)?.workspaceCode ??
		WorkspaceCode.QUEUE_CARD
	)
}
